import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X, Share as ShareIcon, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event("ck-install-ready"));
  });
}

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent || "");

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

export function InstallAppButton({ className = "", variant = "solid" }) {
  const [ready, setReady] = useState(!!deferredPrompt);
  const [showHelp, setShowHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onReady = () => setReady(true);
    const onInstalled = () => { setInstalled(true); setShowHelp(false); deferredPrompt = null; };
    window.addEventListener("ck-install-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("ck-install-ready", onReady);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch (_) { /* user dismissed */ }
      deferredPrompt = null;
      setReady(false);
    } else {
      setShowHelp(true);
    }
  };

  const base = variant === "solid"
    ? "bg-white text-[#0B223D] hover:bg-slate-100 font-bold rounded-full px-7 h-12 text-base"
    : "bg-white/5 border-white/25 text-white hover:bg-white/15 font-semibold rounded-full px-7 h-12 text-base";

  return (
    <>
      <Button onClick={onClick} data-testid="install-app-btn" variant={variant === "solid" ? "default" : "outline"} className={`${base} ${className}`}>
        <Download className="w-4 h-4 mr-1.5" /> Download App
      </Button>
      {showHelp && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowHelp(false)} data-testid="install-help-overlay">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="install-help-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg text-[#0B223D] flex items-center gap-2"><Download className="w-5 h-5 text-teal-600" /> Install Credit Kendra App</h3>
              <button onClick={() => setShowHelp(false)} data-testid="install-help-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            {isIOS() ? (
              <ol className="space-y-3 text-sm text-slate-600">
                <li className="flex gap-2.5"><span className="w-6 h-6 shrink-0 rounded-full bg-[#0B223D] text-white text-xs font-bold flex items-center justify-center">1</span><span>Safari me neeche <ShareIcon className="w-4 h-4 inline text-teal-600" /> <b>Share</b> button dabayein</span></li>
                <li className="flex gap-2.5"><span className="w-6 h-6 shrink-0 rounded-full bg-[#0B223D] text-white text-xs font-bold flex items-center justify-center">2</span><span><b>"Add to Home Screen"</b> par tap karein</span></li>
                <li className="flex gap-2.5"><span className="w-6 h-6 shrink-0 rounded-full bg-[#0B223D] text-white text-xs font-bold flex items-center justify-center">3</span><span><b>Add</b> confirm karein — app home screen par aa jayega</span></li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-slate-600">
                <li className="flex gap-2.5"><span className="w-6 h-6 shrink-0 rounded-full bg-[#0B223D] text-white text-xs font-bold flex items-center justify-center">1</span><span>Browser menu <MoreVertical className="w-4 h-4 inline text-teal-600" /> (3 dots) kholen</span></li>
                <li className="flex gap-2.5"><span className="w-6 h-6 shrink-0 rounded-full bg-[#0B223D] text-white text-xs font-bold flex items-center justify-center">2</span><span><b>"Install app"</b> ya <b>"Add to Home screen"</b> chunein</span></li>
                <li className="flex gap-2.5"><span className="w-6 h-6 shrink-0 rounded-full bg-[#0B223D] text-white text-xs font-bold flex items-center justify-center">3</span><span>Confirm karein — Credit Kendra app install ho jayega</span></li>
              </ol>
            )}
            <p className="text-[11px] text-slate-400 mt-4">Ye ek Progressive Web App hai — Play Store ki zaroorat nahi, seedha browser se install hota hai.</p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
